import React, { createContext, useContext, useState, useEffect } from 'react';
import { 
  GoogleAuthProvider,
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  deleteUser,
  getAdditionalUserInfo,
  sendPasswordResetEmail,
  signInWithPopup,
  signOut,
  onAuthStateChanged
} from 'firebase/auth';
import { doc, getDoc, onSnapshot, setDoc } from 'firebase/firestore';
import { auth, db } from '../config/firebase';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [currentUser, setCurrentUser] = useState(null);
  const [userProfile, setUserProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const googleProvider = new GoogleAuthProvider();

  const normalizeEmail = (email = '') => String(email || '').trim().toLowerCase();
  const normalizeString = (value = '') => String(value || '').trim();
  const splitDisplayName = (displayName = '') => {
    const normalizedName = normalizeString(displayName);
    if (!normalizedName) {
      return { firstName: '', lastName: '' };
    }

    const [firstName = '', ...rest] = normalizedName.split(/\s+/);
    return {
      firstName,
      lastName: rest.join(' ')
    };
  };

  const buildResellerProfilePayload = ({ email, userData = {}, authUser = null }) => {
    const safeUserData = { ...(userData || {}) };
    delete safeUserData.email;
    delete safeUserData.role;
    delete safeUserData.status;
    delete safeUserData.balance;
    delete safeUserData.creditBalance;
    delete safeUserData.lastPurchase;
    delete safeUserData.createdAt;

    const displayNameParts = splitDisplayName(authUser?.displayName);
    const normalizedEmail = normalizeEmail(email || authUser?.email);

    return {
      ...safeUserData,
      email: normalizedEmail,
      firstName: normalizeString(safeUserData.firstName) || displayNameParts.firstName,
      lastName: normalizeString(safeUserData.lastName) || displayNameParts.lastName,
      company: normalizeString(safeUserData.company),
      phone: normalizeString(safeUserData.phone),
      address: normalizeString(safeUserData.address),
      city: normalizeString(safeUserData.city),
      postalCode: normalizeString(safeUserData.postalCode),
      role: 'reseller',
      status: 'pending',
      balance: 0,
      creditBalance: 0,
      createdAt: new Date()
    };
  };

  const ensureResellerProfile = async ({ authUser, email, userData = {}, allowCreate = true }) => {
    const userRef = doc(db, 'users', authUser.uid);
    const existingProfile = await getDoc(userRef);

    if (existingProfile.exists()) {
      const profileData = existingProfile.data() || {};

      if (profileData.role && profileData.role !== 'reseller') {
        const roleError = new Error('La connexion Google est reservee aux comptes revendeurs');
        roleError.code = 'auth/google-role-not-allowed';
        throw roleError;
      }

      return { created: false, profile: { id: authUser.uid, ...profileData } };
    }

    if (!allowCreate) {
      const missingProfileError = new Error('Aucun profil revendeur n est associe a ce compte');
      missingProfileError.code = 'auth/missing-user-profile';
      throw missingProfileError;
    }

    const profilePayload = buildResellerProfilePayload({ email, userData, authUser });
    await setDoc(userRef, profilePayload);

    return { created: true, profile: { id: authUser.uid, ...profilePayload } };
  };

  // Connexion
  const login = async (email, password) => {
    try {
      const result = await signInWithEmailAndPassword(auth, normalizeEmail(email), password);
      return result;
    } catch (error) {
      throw error;
    }
  };

  // Inscription
  const register = async (email, password, userData) => {
    let authResult = null;

    try {
      const normalizedEmail = normalizeEmail(email);
      authResult = await createUserWithEmailAndPassword(auth, normalizedEmail, password);
      await ensureResellerProfile({
        authUser: authResult.user,
        email: normalizedEmail,
        userData,
        allowCreate: true
      });
      
      return authResult;
    } catch (error) {
      if (authResult?.user) {
        try {
          await deleteUser(authResult.user);
        } catch (cleanupError) {
          console.error('Erreur lors du nettoyage du compte auth orphelin:', cleanupError);

          try {
            await signOut(auth);
          } catch (signOutError) {
            console.error('Erreur lors de la deconnexion apres echec de cleanup:', signOutError);
          }
        }
      }

      throw error;
    }
  };

  const resetPassword = async (email) => {
    try {
      await sendPasswordResetEmail(auth, normalizeEmail(email));
      return true;
    } catch (error) {
      throw error;
    }
  };

  const signInWithGoogleForReseller = async () => {
    let authResult = null;

    try {
      googleProvider.setCustomParameters({
        prompt: 'select_account'
      });

      authResult = await signInWithPopup(auth, googleProvider);
      const normalizedEmail = normalizeEmail(authResult.user?.email);
      const additionalUserInfo = getAdditionalUserInfo(authResult);

      await ensureResellerProfile({
        authUser: authResult.user,
        email: normalizedEmail,
        allowCreate: true
      });

      return {
        ...authResult,
        isNewUser: Boolean(additionalUserInfo?.isNewUser)
      };
    } catch (error) {
      if (error.code === 'auth/google-role-not-allowed') {
        try {
          await signOut(auth);
        } catch (signOutError) {
          console.error('Erreur lors de la deconnexion apres refus Google:', signOutError);
        }
      }

      if (authResult?.user && getAdditionalUserInfo(authResult)?.isNewUser) {
        try {
          await deleteUser(authResult.user);
        } catch (cleanupError) {
          console.error('Erreur lors du nettoyage du compte Google orphelin:', cleanupError);

          try {
            await signOut(auth);
          } catch (signOutError) {
            console.error('Erreur lors de la deconnexion apres echec cleanup Google:', signOutError);
          }
        }
      }

      throw error;
    }
  };

  // Déconnexion
  const logout = () => {
    return signOut(auth);
  };

  // Charger le profil utilisateur
  const loadUserProfile = async (uid) => {
    try {
      const userDoc = await getDoc(doc(db, 'users', uid));
      if (userDoc.exists()) {
        const userData = { id: uid, ...userDoc.data() };
        setUserProfile(userData);
      } else {
        setUserProfile(null);
      }
    } catch (error) {
      console.error('Erreur lors du chargement du profil:', error);
      setUserProfile(null);
    }
  };

  useEffect(() => {
    if (!currentUser?.uid) {
      return undefined;
    }

    const userRef = doc(db, 'users', currentUser.uid);
    const unsubscribeProfile = onSnapshot(
      userRef,
      (userDoc) => {
        if (userDoc.exists()) {
          setUserProfile({ id: currentUser.uid, ...userDoc.data() });
        } else {
          setUserProfile(null);
        }
      },
      (error) => {
        console.error('Erreur lors de l\'écoute du profil:', error);
      }
    );

    return unsubscribeProfile;
  }, [currentUser?.uid]);

  // Vérifier si l'utilisateur est admin
  const isAdmin = () => {
    return userProfile?.role === 'admin';
  };

  // Vérifier si l'utilisateur est opérateur
  const isOperator = () => {
    return userProfile?.role === 'operator';
  };

  const hasRole = (roles = []) => {
    const allowedRoles = Array.isArray(roles) ? roles : [roles];
    return allowedRoles.includes(userProfile?.role);
  };

  // Vérifier si l'utilisateur est actif
  const isActive = () => {
    return userProfile?.status === 'active';
  };

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      setCurrentUser(user);
      if (user) {
        await loadUserProfile(user.uid);
      } else {
        setUserProfile(null);
      }
      setLoading(false);
    }, (error) => {
      console.error('Erreur lors du changement d\'état d\'authentification:', error);
      setLoading(false);
    });

    return unsubscribe;
  }, []);

  const value = {
    currentUser,
    userProfile,
    login,
    register,
    resetPassword,
    signInWithGoogleForReseller,
    logout,
    loadUserProfile,
    isAdmin,
    isOperator,
    hasRole,
    isActive,
    loading
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
